import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang } from '../context/LangContext';
import { mockMaps } from '../data/mockMaps';
import InteractiveVotingMap from '../components/InteractiveVotingMap';
import SafeIcon from '../common/SafeIcon';
import { FiDownload, FiFacebook, FiArrowLeft, FiAlertTriangle, FiMove, FiMaximize, FiSquare, FiSmartphone } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';
import { captureMemeBlob } from '../utils/memeExport';
import { EXPORT_WATERMARK_TEXT } from '../constants/branding';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const SHARE_TIMEOUT_MS = 45000;
const MAX_MEME_BYTES = 10 * 1024 * 1024;

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)),
        ms
      );
    }),
  ]);
}

const ASPECT_RATIOS = {
  WIDE: { name: 'Wide', class: 'aspect-video', ratio: 16/9 },
  SQUARE: { name: 'Square', class: 'aspect-square', ratio: 1/1 },
  STORY: { name: 'Story', class: 'aspect-[9/16] max-w-[400px]', ratio: 9/16 }
};

export default function MemeStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const mapData = mockMaps.find(m => m.id === id) || mockMaps[0];
  const memeRef = useRef(null);

  const [aspect, setAspect] = useState(ASPECT_RATIOS.WIDE);
  const [memeOverrides, setMemeOverrides] = useState({});
  const [sharing, setSharing] = useState(false);
  const [captions, setCaptions] = useState([
    { id: 'top', text: mapData.defaultMemeText || "WHEN YOU REALIZE...", x: 0, y: 20 },
    { id: 'bottom', text: "", x: 0, y: 80 }
  ]);

  const handleMemeOverride = (geoId) => {
    setMemeOverrides((prev) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(prev, geoId);
      const current = hasOverride ? prev[geoId] : null;

      if (current === null) return { ...prev, [geoId]: 'A' };
      if (current === 'A') return { ...prev, [geoId]: 'B' };
      const next = { ...prev };
      delete next[geoId];
      return next;
    });
  };

  const updateCaption = (id, text) => {
    setCaptions(prev => prev.map(c => c.id === id ? { ...c, text } : c));
  };

  const generateMemeBlob = async () => {
    if (!memeRef.current) throw new Error('Meme canvas not ready');
    const blob = await captureMemeBlob(memeRef.current);
    if (blob.size > MAX_MEME_BYTES) {
      throw new Error(`Meme image too large (${Math.round(blob.size / 1024 / 1024)}MB)`);
    }
    return blob;
  };

  const handleDownload = async () => {
    try {
      const blob = await withTimeout(generateMemeBlob(), SHARE_TIMEOUT_MS, 'Image export');
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `votenations-meme-${Date.now()}.png`;
      link.href = objectUrl;
      link.click();
      URL.revokeObjectURL(objectUrl);
      toast.success(t('meme_success'));
    } catch (err) {
      console.error('Meme download failed:', err?.message ?? err, err);
      toast.error(t('meme_export_error'));
    }
  };

  const handleFacebookShare = async () => {
    if (!isSupabaseConfigured) {
      toast.error(t('meme_share_error'));
      return;
    }
    if (!mapData.supabaseId) {
      toast.error(t('meme_share_error'));
      return;
    }

    setSharing(true);
    try {
      await withTimeout((async () => {
        const blob = await generateMemeBlob();
        const fileName = `${crypto.randomUUID()}-${Date.now()}.png`;

        const { error: uploadError } = await supabase.storage
          .from('meme-exports')
          .upload(fileName, blob, { contentType: 'image/png', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('meme-exports').getPublicUrl(fileName);

        const { data: meme, error: insertError } = await supabase
          .from('memes')
          .insert({
            voting_map_id: mapData.supabaseId,
            title_text: captions[0]?.text?.trim() || null,
            overrides: memeOverrides,
            image_url: urlData.publicUrl,
          })
          .select('id')
          .single();

        if (insertError) throw insertError;

        const previewUrl = `${window.location.origin}/meme/${meme.id}`;
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(previewUrl)}`,
          '_blank',
          'width=600,height=400'
        );
      })(), SHARE_TIMEOUT_MS, 'Facebook share');
    } catch (err) {
      console.error('Facebook share failed:', err?.message ?? err, err);
      toast.error(t('meme_share_error'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <div className="bg-slate-900 border-b border-slate-800 p-4 flex justify-between items-center z-20">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white font-bold">
          <SafeIcon icon={FiArrowLeft} /> Back
        </button>
        <h2 className="text-xl font-black uppercase tracking-widest text-fuchsia-500 text-glow">
          {t('meme_studio')}
        </h2>
        <div className="w-20" />
      </div>

      <div className="flex-1 p-4 md:p-8 flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto w-full">
        {/* Canvas Area */}
        <div className="flex-1 flex items-center justify-center bg-slate-900/50 rounded-2xl border-2 border-dashed border-slate-700 p-4 relative overflow-hidden">
          <div 
            ref={memeRef}
            className={cn(
              "relative bg-slate-900 overflow-hidden shadow-2xl flex items-center justify-center",
              aspect.class
            )}
            style={{ width: '100%' }}
          >
            {/* Draggable Captions */}
            {captions.map((cap) => (
              <motion.div
                key={cap.id}
                drag
                dragConstraints={memeRef}
                dragMomentum={false}
                className="absolute z-30 cursor-move py-2 px-4 touch-none"
                style={{ top: `${cap.y}%`, left: '0', right: '0' }}
              >
                <div className="flex flex-col items-center group">
                  <div className="hidden group-hover:flex absolute -top-6 items-center gap-1 bg-slate-950 px-2 py-0.5 rounded text-[10px] font-bold text-slate-400">
                    <SafeIcon icon={FiMove} /> DRAG ME
                  </div>
                  <h2 
                    className="font-black text-2xl md:text-5xl text-center uppercase tracking-tighter break-words max-w-full"
                    style={{ 
                      color: 'white',
                      textShadow: '3px 3px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0 5px 15px rgba(0,0,0,0.8)',
                      lineHeight: '1'
                    }}
                  >
                    {cap.text}
                  </h2>
                </div>
              </motion.div>
            ))}

            <InteractiveVotingMap
              mapData={mapData}
              isMemeMode={true}
              memeOverrides={memeOverrides}
              onMemeOverride={handleMemeOverride}
              hideMapHints={true}
            />

            {/* Branding & Watermark */}
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end z-30 pointer-events-none">
              <div className="flex items-center gap-2 bg-yellow-500/90 text-slate-950 px-2 py-0.5 rounded font-bold text-[10px] uppercase">
                <SafeIcon icon={FiAlertTriangle} /> {t('meme_warning')}
              </div>
              <div className="font-black italic text-slate-500/50 text-xl uppercase tracking-tighter">
                {EXPORT_WATERMARK_TEXT}
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="w-full lg:w-80 flex flex-col gap-6">
          <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 space-y-6">
            <div>
              <h3 className="font-bold text-sm mb-3 uppercase text-slate-400 tracking-wider">Format</h3>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'WIDE', icon: FiMaximize },
                  { id: 'SQUARE', icon: FiSquare },
                  { id: 'STORY', icon: FiSmartphone }
                ].map(({ id, icon }) => (
                  <button
                    key={id}
                    onClick={() => setAspect(ASPECT_RATIOS[id])}
                    className={cn(
                      "flex flex-col items-center gap-1 p-2 rounded border transition-all",
                      aspect === ASPECT_RATIOS[id] ? "bg-fuchsia-500/20 border-fuchsia-500 text-fuchsia-500" : "bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-600"
                    )}
                  >
                    <SafeIcon icon={icon} />
                    <span className="text-[10px] font-bold uppercase">{ASPECT_RATIOS[id].name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-bold text-sm mb-3 uppercase text-slate-400 tracking-wider">Captions</h3>
              <div className="space-y-4">
                {captions.map(cap => (
                  <div key={cap.id}>
                    <label className="text-[10px] font-black uppercase text-slate-500 mb-1 block">
                      {cap.id} Caption
                    </label>
                    <textarea
                      value={cap.text}
                      onChange={(e) => updateCaption(cap.id, e.target.value)}
                      placeholder={`Enter ${cap.id} text...`}
                      className="w-full bg-slate-950 border border-slate-800 rounded p-2 text-sm text-white focus:border-fuchsia-500 outline-none resize-none transition-colors"
                      rows={2}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800 space-y-3">
              <button
                type="button"
                onClick={handleFacebookShare}
                disabled={sharing}
                className={cn(
                  "flex items-center justify-center gap-2 w-full py-3 bg-[#1877F2] text-white font-bold rounded transition-colors text-sm",
                  sharing ? "opacity-70 cursor-not-allowed" : "hover:bg-[#166fe5]"
                )}
              >
                <SafeIcon icon={FiFacebook} /> {sharing ? t('meme_sharing') : t('share')}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={sharing}
                className="flex items-center justify-center gap-2 w-full py-3 bg-white hover:bg-slate-100 text-slate-950 font-bold rounded transition-colors text-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiDownload} /> {t('download')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}